import { ElementPageViewer } from "@/components/ElementPage";
import UserComments from "@/components/UserComments";
import configuration from "@/configuration";
import { useWindowSize } from "@/hooks";
import { TemplateContent, TemplateSection } from "@/services/admin-services/models";
import { PageType } from "@/services/shared-models";
import { ElementService } from "@/services/user-services";
import { ElementPage } from "@/services/user-services/models";
import { useDataStore, useFilterStore, useLocaleStore } from "@/stores";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrianglePersonDigging } from "@fortawesome/sharp-light-svg-icons";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import ElementLoading from "./components/ElementLoading";

const Element = () => {
    const activeRegion = useLocaleStore((state) => state.activeRegion);
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);

    const versions = useDataStore((state) => state.versions);
    const elements = useDataStore((state) => state.elements);

    const activeVersion = useFilterStore((state) => state.activeVersion);
    const selectedDisciplines = useFilterStore((state) => state.activeDisciplines);

    const [width] = useWindowSize();
    const [left, setLeft] = useState(0);
    const [loading, setLoading] = useState(false);

    const elementService = useMemo(
        () => new ElementService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const params = useParams();
    const slug = params["*"]?.split("/")[1] || "";

    const [template, setTemplate] = useState<TemplateContent>();
    const [pageContent, setPageContent] = useState<ElementPage | null>(null);
    const [image, setImage] = useState<string | null>(null);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, []);

    useEffect(() => {
        if (loading || !slug) return;

        setLoading(true);

        let activeVersionName = versions.find((x) => x.id === activeVersion)?.name || "";
        if (!activeVersionName && activeVersion === "wip") {
            activeVersionName = "wip";
        }

        elementService
            .getPage(slug, activeVersionName)
            .then((page) => {
                if (!page) {
                    return;
                }

                const sectionsOrder: Array<string> = JSON.parse(page?.templateContent);
                const sections = page.templateSections.map((x) => ({
                    ...x,
                    order: sectionsOrder.indexOf(x.sectionCode),
                }));

                setPageContent({ ...page, version: activeVersionName });
                setTemplate({
                    languageCode: slug!,
                    content: page.templateContent,
                    sections: sections.sort((a, b) => a.order - b.order) as Array<TemplateSection>,
                });

                setImage(page.image);
            })
            .then(() => {
                setLoading(false);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, activeVersion]);

    useLayoutEffect(() => {
        if (width === 2560) {
            setLeft(0);
        } else {
            const change = 2560 - width;
            setLeft(change * -0.5);
        }
    }, [width]);

    const { t } = useTranslation("elements");

    if (loading || !pageContent) return <ElementLoading />;

    return (
        <>
            <Helmet>
                <title>BIMids - {pageContent!.name}</title>
                <meta name="title" content={pageContent!.name} />
                <meta name="description" content={pageContent!.description} />

                <meta property="og:title" content={pageContent!.name} />
                <meta property="og:description" content={pageContent!.description} />

                <meta property="twitter:title" content={pageContent!.name} />
                <meta property="twitter:description" content={pageContent!.description} />
            </Helmet>

            <div
                className={`absolute w-full h-full bg-primary-100 overflow-hidden`}
                style={{
                    left: `${left}px`,
                    width: 2015,
                    clipPath: "polygon(200px 0, 2015px 0, 838px 450px)",
                }}
            />

            <div className="container mx-auto 2xl:px-[108px]">
                <section id="hero" className="relative pt-[60px] mb-1">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4 mb-4">
                        <div className="flex flex-col mt-3">
                            <Link
                                to={`/${params.locale}/elements`}
                                className="flex w-fit items-center gap-2 mb-4 text-primary-500 hover:text-primary-600 hover:underline select-none cursor-pointer"
                            >
                                <ChevronLeftIcon className="w-4 h-4" /> {t("return", { ns: "elements" })}
                            </Link>
                            <div>
                                <div className="flex items-center gap-4">
                                    <h1 className="content-center space-y-4 leading-none">
                                        <span className="text-primary-500">{pageContent.name}</span>
                                    </h1>

                                    {pageContent.isCompleted === false && (
                                        <div className="shrink-0">
                                            <FontAwesomeIcon
                                                icon={faTrianglePersonDigging}
                                                aria-hidden="true"
                                                className="text-5xl text-primary-500"
                                            />
                                        </div>
                                    )}
                                </div>
                                <p className="text-lg/[32px] font-medium mt-4 max-w-[920px]">
                                    {pageContent.ifcClass && (
                                        <span>
                                            {t("subtitle", { ns: "elements" })}{" "}
                                            {/* <a
                                                href={`https://standards.buildingsmart.org/IFC/DEV/IFC4_2/FINAL/HTML/schema/ifcbuildingcontrolsdomain/lexical/${pageContent.ifcClass}.htm`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-bold text-primary-500 hover:text-primary-600 hover:underline cursor-pointer"
                                            > */}
                                            {pageContent.ifcClass}
                                            {/* </a> */}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div>
                            {image && (
                                <img
                                    className="flex items-center justify-center w-full bg-gray-300 rounded-3xl"
                                    src={`${configuration.assets}${image}`}
                                />
                            )}
                        </div>
                    </div>
                </section>
                <main>
                    <div className="relative grid grid-cols-[auto_minmax(100px,1fr)] gap-14">
                        <ElementPageViewer
                            page={pageContent}
                            template={template!}
                            disciplines={selectedDisciplines}
                            elements={elements[activeVersion!] || []}
                        >
                            <div className="col-start-2 mt-15">
                                <UserComments comments={pageContent!.comments || []} pageType={PageType.Elements} />
                            </div>
                        </ElementPageViewer>
                    </div>
                </main>
            </div>

            {/* <Spotlight>
                <h2 className="text-center text-black">
                    Découvrez tous
                    <br />
                    <span className="text-primary-500">nos éléments BIM</span>
                </h2>
                <button className="flex text-white font-medium text-l px-8.5 py-5.5 bg-primary-500 hover:bg-primary-600 cursor-pointer rounded-lg mx-auto gap-3">
                    Téléchargez plus de 100 éléments BIM
                </button>
            </Spotlight> */}
        </>
    );
};

export default Element;
